import { useId } from "react";
import { Plus, Trash2, Target, Users } from "lucide-react";

const inputClass =
  "mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

const labelClass = "text-sm font-medium text-slate-700";

function Field({
  label,
  name,
  value,
  onChange,
  type = "text",
  placeholder = "",
  required = false,
  disabled = false,
  min,
  step,
}) {
  return (
    <label className="block">
      <span className={labelClass}>
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </span>

      <input
        type={type}
        name={name}
        value={value ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        min={min}
        step={step}
        className={`${inputClass} ${
          disabled ? "cursor-not-allowed bg-slate-100 text-slate-500" : ""
        }`}
      />
    </label>
  );
}

// Suggestions are optional: users may select an option or enter their own value.
function SelectField({
  label,
  name,
  value,
  onChange,
  options = [],
  required = false,
}) {
  const listId = useId();

  return (
    <label className="block">
      <span className={labelClass}>
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </span>

      <input
        type="text"
        name={name}
        value={value ?? ""}
        onChange={onChange}
        list={listId}
        autoComplete="off"
        placeholder={`Choose or enter ${label.toLowerCase()}`}
        className={inputClass}
      />
      <datalist id={listId}>
        {options.map((option) => (
          <option key={option} value={option} />
        ))}
      </datalist>
    </label>
  );
}

function TextAreaField({
  label,
  name,
  value,
  onChange,
  placeholder = "",
  rows = 4,
}) {
  return (
    <label className="block">
      <span className={labelClass}>{label}</span>

      <textarea
        name={name}
        value={value ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        className={`${inputClass} resize-y`}
      />
    </label>
  );
}

function SectionCard({ title, description, children }) {
  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 bg-slate-50 px-5 py-4">
        <h3 className="text-base font-semibold text-slate-900">{title}</h3>

        {description && (
          <p className="mt-1 text-sm text-slate-500">{description}</p>
        )}
      </div>

      <div className="p-5">{children}</div>
    </section>
  );
}

export default function SummaryStrategyForm({ data, onChange }) {
  const handleFieldChange = (event) => {
    const { name, value } = event.target;

    onChange({
      ...data,
      [name]: value,
    });
  };

  const updateCompetitor = (index, field, value) => {
    const updatedCompetitors = data.competitors.map((competitor, rowIndex) =>
      rowIndex === index
        ? {
            ...competitor,
            [field]: value,
          }
        : competitor
    );

    onChange({
      ...data,
      competitors: updatedCompetitors,
    });
  };

  const addCompetitor = () => {
    onChange({
      ...data,
      competitors: [
        ...data.competitors,
        {
          bidderName: "",
          oemName: "",
        },
      ],
    });
  };

  const removeCompetitor = (index) => {
    if (data.competitors.length === 1) return;

    onChange({
      ...data,
      competitors: data.competitors.filter(
        (_, rowIndex) => rowIndex !== index
      ),
    });
  };

  const updateContact = (index, field, value) => {
    const updatedContacts = data.contacts.map((contact, rowIndex) =>
      rowIndex === index
        ? {
            ...contact,
            [field]: value,
          }
        : contact
    );

    onChange({
      ...data,
      contacts: updatedContacts,
    });
  };

  const calculateScore = () => {
    let score = 0;

    const bidTypeScores = {
      "Managed (50 pts)": 50,
      "Pro active (40 pts)": 40,
      "Partially active (30 pts)": 30,
      "Reactive (20 pts)": 20,
    };

    const emdScores = {
      "0 (10 pts)": 10,
      "1% of Bid value (5 pts)": 5,
      "2% or higher (0 pts)": 0,
    };

    const paymentScores = {
      "More than 75% against Delivery (20 pts)": 20,
      "50 - 75% against delivery (15 pts)": 15,
      "Against Implementation (10 pts)": 10,
    };

    const raScores = {
      "No (10 pts)": 10,
      "Yes (0 pts)": 0,
    };

    const pbgScores = {
      "3% (10 pts)": 10,
      "5% (5 pts)": 5,
      "10% (0 pts)": 0,
    };

    score += bidTypeScores[data.bidType] || 0;
    score += emdScores[data.emdSelection] || 0;
    score += paymentScores[data.paymentTerms] || 0;
    score += raScores[data.raModelApplicable] || 0;
    score += pbgScores[data.pbgPercentage] || 0;

    return score;
  };

  const calculateWinningProbability = () => {
    const score = calculateScore();
    const competitors = Number(data.competitiveBidCount || 0);

    let competitionFactor = 1;

    if (competitors === 5) {
      competitionFactor = 0.8;
    } else if (competitors > 5) {
      competitionFactor = 0.5;
    }

    const supportFactors = {
      "Both (Customer and OEM)": 1,
      Customer: 0.85,
      OEM: 0.85,
      None: 0.8,
    };

    const supportFactor = supportFactors[data.supportedBy] || 0.8;

    return Math.min(
      95,
      Math.round(score * competitionFactor * supportFactor)
    );
  };

  const totalScore = calculateScore();
  const winningProbability = calculateWinningProbability();

  const scoreColor =
    totalScore >= 75
      ? "text-emerald-700"
      : totalScore >= 50
      ? "text-amber-700"
      : "text-red-700";

  const probabilityColor =
    winningProbability >= 70
      ? "text-emerald-700"
      : winningProbability >= 40
      ? "text-amber-700"
      : "text-red-700";

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">
                Total Quantitative Score
              </p>

              <p className={`mt-2 text-3xl font-bold ${scoreColor}`}>
                {totalScore}
                <span className="ml-1 text-base font-medium text-slate-400">
                  / 100
                </span>
              </p>
            </div>

            <div className="rounded-xl bg-blue-100 p-3 text-blue-700">
              <Target size={25} />
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500">
                Estimated Winning Probability
              </p>

              <p className={`mt-2 text-3xl font-bold ${probabilityColor}`}>
                {winningProbability}%
              </p>
            </div>

            <div className="rounded-xl bg-emerald-100 p-3 text-emerald-700">
              <Users size={25} />
            </div>
          </div>
        </div>
      </div>

      <SectionCard
        title="Opportunity Information"
        description="Basic tender, customer and opportunity details."
      >
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          <Field
            label="Opportunity ID"
            name="opportunityId"
            value={data.opportunityId}
            onChange={handleFieldChange}
            disabled
          />

          <Field
            label="Opportunity Date"
            name="opportunityDate"
            value={data.opportunityDate}
            onChange={handleFieldChange}
            type="date"
          />

          <Field
            label="Tender Reference"
            name="tenderReference"
            value={data.tenderReference}
            onChange={handleFieldChange}
            placeholder="Enter tender/RFP reference"
          />

          <Field
            label="Project Name"
            name="projectName"
            value={data.projectName}
            onChange={handleFieldChange}
            placeholder="Enter project name"
            required
          />

          <Field
            label="Due Date and Time"
            name="dueDateTime"
            value={data.dueDateTime}
            onChange={handleFieldChange}
            type="datetime-local"
          />

          <Field
            label="Customer Name"
            name="customerName"
            value={data.customerName}
            onChange={handleFieldChange}
            placeholder="Enter customer name"
            required
          />

          <Field
            label="Account Manager"
            name="accountManager"
            value={data.accountManager}
            onChange={handleFieldChange}
            placeholder="Enter account manager"
          />

          <Field
            label="Pre-Sales Lead"
            name="presalesLead"
            value={data.presalesLead}
            onChange={handleFieldChange}
            placeholder="Enter pre-sales lead"
          />

          <Field
            label="Bid Manager"
            name="bidManager"
            value={data.bidManager}
            onChange={handleFieldChange}
            placeholder="Enter bid manager"
          />
        </div>
      </SectionCard>

      <SectionCard
        title="Bid Strategy"
        description="Information used to evaluate the bid and estimate the probability of winning."
      >
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          <SelectField
            label="Bid Type"
            name="bidType"
            value={data.bidType}
            onChange={handleFieldChange}
            options={[
              "Managed (50 pts)",
              "Pro active (40 pts)",
              "Partially active (30 pts)",
              "Reactive (20 pts)",
            ]}
            required
          />

          <SelectField
            label="Supported By"
            name="supportedBy"
            value={data.supportedBy}
            onChange={handleFieldChange}
            options={[
              "Both (Customer and OEM)",
              "OEM",
              "Customer",
              "None (Neither Customer nor OEM)",
            ]}
          />

          <Field
            label="Expected Number of Competitive Bids"
            name="competitiveBidCount"
            value={data.competitiveBidCount}
            onChange={handleFieldChange}
            type="number"
            min="0"
            placeholder="0"
          />

          <Field
            label="Delivery Timelines"
            name="deliveryTimeline"
            value={data.deliveryTimeline}
            onChange={handleFieldChange}
            placeholder="Example: 8–10 weeks"
          />
        </div>

        <div className="mt-5">
          <TextAreaField
            label="Winning Strategy"
            name="winningStrategy"
            value={data.winningStrategy}
            onChange={handleFieldChange}
            placeholder="Describe the proposed winning strategy, key differentiators and customer priorities."
            rows={5}
          />
        </div>
      </SectionCard>

      <SectionCard
        title="Expected Competitors"
        description="Add the expected competitive bidders and their proposed OEMs."
      >
        <div className="space-y-3">
          {data.competitors.map((competitor, index) => (
            <div
              key={index}
              className="grid items-end gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 md:grid-cols-[60px_1fr_1fr_44px]"
            >
              <div>
                <p className={labelClass}>S.No.</p>

                <div className="mt-1.5 flex h-[42px] items-center justify-center rounded-lg border border-slate-200 bg-white text-sm font-semibold text-slate-600">
                  {index + 1}
                </div>
              </div>

              <Field
                label="Bidder Name"
                value={competitor.bidderName}
                onChange={(event) =>
                  updateCompetitor(
                    index,
                    "bidderName",
                    event.target.value
                  )
                }
                placeholder="Enter bidder name"
              />

              <Field
                label="OEM Name"
                value={competitor.oemName}
                onChange={(event) =>
                  updateCompetitor(index, "oemName", event.target.value)
                }
                placeholder="Enter OEM name"
              />

              <button
                type="button"
                onClick={() => removeCompetitor(index)}
                disabled={data.competitors.length === 1}
                className="flex h-[42px] items-center justify-center rounded-lg border border-red-200 bg-white text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
                title="Remove competitor"
              >
                <Trash2 size={17} />
              </button>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={addCompetitor}
          className="mt-4 inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 transition hover:bg-blue-100"
        >
          <Plus size={17} />
          Add Competitor
        </button>
      </SectionCard>

      <SectionCard
        title="Contact Details"
        description="Enter customer, OEM, MSI and consultant contact details."
      >
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="min-w-[1050px] w-full">
            <thead className="bg-slate-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Contact Type
                </th>

                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Contact Person
                </th>

                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Email ID
                </th>

                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                  Contact Number
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-200 bg-white">
              {data.contacts.map((contact, index) => (
                <tr key={`${contact.contactType}-${index}`}>
                  <td className="px-4 py-3">
                    <span className="inline-flex rounded-md bg-blue-50 px-2.5 py-1 text-sm font-semibold text-blue-700">
                      {contact.contactType}
                    </span>
                  </td>

                  <td className="px-4 py-3">
                    <input
                      type="text"
                      value={contact.contactPerson}
                      onChange={(event) =>
                        updateContact(
                          index,
                          "contactPerson",
                          event.target.value
                        )
                      }
                      className={inputClass}
                      placeholder="Contact person"
                    />
                  </td>

                  <td className="px-4 py-3">
                    <input
                      type="email"
                      value={contact.email}
                      onChange={(event) =>
                        updateContact(index, "email", event.target.value)
                      }
                      className={inputClass}
                      placeholder="name@example.com"
                    />
                  </td>

                  <td className="px-4 py-3">
                    <input
                      type="tel"
                      value={contact.phone}
                      onChange={(event) =>
                        updateContact(index, "phone", event.target.value)
                      }
                      className={inputClass}
                      placeholder="Contact number"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <SectionCard
        title="Commercial Evaluation"
        description="These selections contribute to the quantitative bid score."
      >
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          <Field
            label="Project Value"
            name="projectValue"
            value={data.projectValue}
            onChange={handleFieldChange}
            type="number"
            min="0"
            step="0.01"
            placeholder="Enter amount"
          />

          <SelectField
            label="EMD Selection"
            name="emdSelection"
            value={data.emdSelection}
            onChange={handleFieldChange}
            options={[
              "0 (10 pts)",
              "1% of Bid value (5 pts)",
              "2% or higher (0 pts)",
            ]}
          />

          <SelectField
            label="Payment Terms"
            name="paymentTerms"
            value={data.paymentTerms}
            onChange={handleFieldChange}
            options={[
              "More than 75% against Delivery (20 pts)",
              "50 - 75% against delivery (15 pts)",
              "Against Implementation (10 pts)",
            ]}
          />

          <SelectField
            label="RA Model Applicable"
            name="raModelApplicable"
            value={data.raModelApplicable}
            onChange={handleFieldChange}
            options={["No (10 pts)", "Yes (0 pts)"]}
          />

          <SelectField
            label="PBG Requirement"
            name="pbgPercentage"
            value={data.pbgPercentage}
            onChange={handleFieldChange}
            options={[
              "3% (10 pts)",
              "5% (5 pts)",
              "10% (0 pts)",
            ]}
          />

          <Field
            label="EMD Value"
            name="emdValue"
            value={data.emdValue}
            onChange={handleFieldChange}
            type="number"
            min="0"
            step="0.01"
            placeholder="Enter EMD value"
          />

          <SelectField
            label="EMD Type"
            name="emdType"
            value={data.emdType}
            onChange={handleFieldChange}
            options={["DD", "BG", "NEFT Transfer"]}
          />
        </div>
      </SectionCard>

      <SectionCard
        title="Important Dates"
        description="Record all tender milestones and submission deadlines."
      >
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          <Field
            label="Pre-Bid Date"
            name="preBidDate"
            value={data.preBidDate}
            onChange={handleFieldChange}
            type="date"
          />

          <Field
            label="Clarification Deadline"
            name="clarificationDeadline"
            value={data.clarificationDeadline}
            onChange={handleFieldChange}
            type="date"
          />

          <Field
            label="Bid Submission Date"
            name="bidSubmissionDate"
            value={data.bidSubmissionDate}
            onChange={handleFieldChange}
            type="date"
          />

          <Field
            label="Expected Award Date"
            name="expectedAwardDate"
            value={data.expectedAwardDate}
            onChange={handleFieldChange}
            type="date"
          />
        </div>
      </SectionCard>

      <SectionCard
        title="Final Bid Decision"
        description="Enter the final recommendation and approved commercial margin."
      >
        <div className="grid gap-5 md:grid-cols-2">
          <SelectField
            label="Go / No-Go Status"
            name="goNoGoStatus"
            value={data.goNoGoStatus}
            onChange={handleFieldChange}
            options={[
              "Go",
              "Conditional Go",
              "No-Go",
              "Pending Approval",
            ]}
          />

          <Field
            label="Approved Net Margin (%)"
            name="approvedNetMargin"
            value={data.approvedNetMargin}
            onChange={handleFieldChange}
            type="number"
            min="0"
            step="0.01"
            placeholder="Example: 7"
          />
        </div>

        <div className="mt-5">
          <TextAreaField
            label="Justification if Margin is Less Than 6%"
            name="marginJustification"
            value={data.marginJustification}
            onChange={handleFieldChange}
            placeholder="Provide the business justification for accepting a net margin below 6%."
            rows={4}
          />
        </div>
      </SectionCard>
    </div>
  );
}
